import React, { useState, useCallback } from 'react';
import { Container, Spinner, Button } from 'react-bootstrap';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import SocialFeedItem from './SocialFeedItem';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import { useSocialFeed } from '../../hooks/useSocialFeed';
import { CollectionModal } from '../../hooks/useCollections';
import useCollections from '../../hooks/useCollections';
import './SocialFeedItem.css';

/**
 * SocialPage - Displays social media posts from government officials via GovSky RSS
 */
const SocialPage = () => {
  const { t } = useTranslation('common');

  // State management
  const [feedLimit, setFeedLimit] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  // RSS Feed URL
  const feedUrl = 'https://govsky.app/api/rss/us/officials';

  // Use our custom RSS feed hook
  const { 
    data, 
    loading, 
    error, 
    feedInfo,
    totalCount,
    displayedCount,
    refresh,
    hasMore 
  } = useSocialFeed(feedUrl, feedLimit, searchQuery);

  // Collection hook for modal
  const collectionHook = useCollections();

  // Handle search
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setFeedLimit(20); // Reset limit when searching
  }, []);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    setFeedLimit(prev => prev + 20);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      {/* Navigation Bar with Search */}
      <IntegratedSearchNavBar
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        currentCountry="us"
        placeholder={t('common:searchPlaceholder', 'Search...')}
      />

      <div id="homebg">
        <Container style={{ paddingBottom: '100px' }}>
          <div className="social-page-content">
         
            {/* Page Header
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 style={{ color: 'white', marginBottom: '4px' }}>
                  {feedInfo?.title || 'Officials Social Feed'}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: 0 }}>
                  Latest posts from government officials
                </p>
              </div>
              <Button 
                onClick={handleRefresh} 
                className="clickable"
                id="billLink"
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <ArrowClockwise className={loading ? 'spin-animation' : ''} />
                Refresh
              </Button>
            </div> */}

            {/* Feed Content */}
            {loading && data.length === 0 ? (
              <LoadingState />
            ) : error ? (
              <ErrorState error={error} onRetry={refresh} />
            ) : data.length === 0 ? (
              <EmptyState searchQuery={searchQuery} />
            ) : (
              <>
                {/* Feed Items */}
                <div className="social-feed-list">
                  {data.map((item) => (
                    <SocialFeedItem
                      key={item.id}
                      item={item}
                      variant="default"
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="d-flex justify-content-center py-4">
                    <Button 
                      className="clickable" 
                      id="feedViewMoreButton" 
                      onClick={handleLoadMore}
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner animation="grow" variant="light" size="sm" />
                      ) : (
                        t('common:loadMore', 'Load More')
                      )}
                    </Button>
                  </div>
                )}

                {/* Feed Stats */}
                <div className="text-center mt-3" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                  {searchQuery
                    ? t('common:showingPostsMatchingQuery', 'Showing {{displayed}} of {{total}} posts matching "{{query}}"', { displayed: displayedCount, total: totalCount, query: searchQuery })
                    : t('common:showingPostsCount', 'Showing {{displayed}} of {{total}} posts', { displayed: displayedCount, total: totalCount })}
                </div>
              </>
            )}
          </div>
        </Container>

        {/* Collection Modal */}
        <CollectionModal
          isModalOpen={collectionHook.isModalOpen}
          currentItem={collectionHook.currentItem}
          saveSuccess={collectionHook.saveSuccess}
          note={collectionHook.note}
          setNote={collectionHook.setNote}
          handleSave={collectionHook.handleSave}
          closeModal={collectionHook.closeModal}
          isStorageAvailable={collectionHook.isStorageAvailable}
        />
      </div>

      {/* Spin animation for refresh button */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-animation {
          animation: spin 1s linear infinite;
        }

        /* Match the Glance column width on the homepage so post cards read as
           cards rather than full-width bars on wide desktop screens. */
        .social-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }
      `}</style>
    </>
  );
};

// Loading State Component
const LoadingState = () => (
  <div className="social-loading">
    <Spinner id="spinner" animation="grow" variant="light" />
  </div>
);

// Error State Component
// error.message renders raw (English from JS runtime); only chrome around it is translated
const ErrorState = ({ error, onRetry }) => {
  const { t } = useTranslation('common');
  return (
    <div className="social-error">
      <h4>{t('common:unableToLoadFeed', 'Unable to load feed')}</h4>
      <p>{error}</p>
      <Button 
        className="clickable mt-3" 
        id="billLink" 
        onClick={onRetry}
      >
        {t('common:tryAgain', 'Try Again')}
      </Button>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ searchQuery }) => {
  const { t } = useTranslation('common');
  return (
    <div className="social-empty">
      {searchQuery ? (
        <>
          <h4>{t('common:noResultsFound', 'No results found')}</h4>
          <p>{t('common:noPostsMatch', 'No posts match "{{query}}"', { query: searchQuery })}</p>
        </>
      ) : (
        <>
          <h4>{t('common:noPostsAvailable', 'No posts available')}</h4>
          <p>{t('common:checkBackSoon', 'Check back soon')}</p>
        </>
      )}
    </div>
  );
};

export default SocialPage;